// src/components/ExerciseSolution.tsx
import React, { useMemo, useState } from 'react';
import MathExpressionNew from '../../components/common/mathjax';
import Button from '../../components/button';
import { Loader } from "../../assets/images/index";
import { Link, Radio } from '@mui/material';
import ChatFooter from "../../components/chat-footer";
import { hebrewLetters } from '../../configs/config';
import { SearchExerciseService } from '../../services/search-exercise/search-exercise.service';
import { showErrorToast, showSuccessToast } from "../../components/Toaster/index";
import ConfirmationModal from '../../components/confirmation-modal';
import "./exercise-solution.scss";

const ExerciseSolution = ({
    exerciseSolution,
    exerciseCharts,
    exerciseQuestions,
    isUpdating,
    selectedExercise,
    t,
    onUpdate,
    onOpenChart,
    updateChart
}: {
    exerciseSolution: string;
    exerciseCharts: { question_id: number, chart_image_base64: string }[];
    exerciseQuestions: { id: number; question: string }[];
    isUpdating: boolean;
    selectedExercise: any;
    t: Function;
    onUpdate: () => void;
    onOpenChart: (img: string) => void;
    updateChart: (questionId: number, chartImage: string) => void;
}) => {
    const searchExerciseService = new SearchExerciseService();
    const [messageInput, setMessageInput] = useState("");
    const [chartRepaintInput, setChartRepaintInput] = useState("");
    const [updatedSolution, setUpdatedSolution] = useState<string | null>(null);
    const [isLoadingExistingSolution, setIsLoadingExistingSolution] = useState(false);
    const [isSendingRequest, setIsSendingRequest] = useState(false);
    const [isUpdateSolution, setIsUpdateSolution] = useState(false);
    const isValidMessage = messageInput.trim().length >= 20;
    const isValidChartRepaintMessage = chartRepaintInput.trim().length >= 20;
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [confirmationLoading, setConfirmationLoading] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
    const [loadingChartId, setLoadingChartId] = useState<number | null>(null);
    const [isChartRepainting, setIsChartRepainting] = useState(false);
    const [confirmationData, setConfirmationData] = useState<{
        title?: string;
        message: string;
        onConfirm: () => Promise<void> | void;
    }>({ message: '', onConfirm: () => {} });  

    const renderedSolution = useMemo(() => {
        return (
            <div id="scoped-db-content">
                <MathExpressionNew content={updatedSolution ? updatedSolution : exerciseSolution} is_Solution={true} />
            </div>
        );
    }, [exerciseSolution, updatedSolution]);

      const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === "Enter" && e.shiftKey) {
          e.preventDefault();
          setMessageInput((prev) => prev + "\n");
        }
      };

    const handleSendRequest = async () => {
        if (!messageInput.trim()) return;

        try {
            setIsSendingRequest(true);
            const payload = {
                exercise_id: selectedExercise?.id,
                existing_solution: updatedSolution || exerciseSolution,
                user_prompt: messageInput
            };

            const response = await searchExerciseService.getFixedSolutionFromLLM(payload);
            console.log(response);
            if (response?.updated_solution) {
                setUpdatedSolution(response.updated_solution);
            }
        } catch (error) {
            console.error("LLM solution generation failed:", error);
        } finally {
            setIsSendingRequest(false);
        }
    };

    const handleViewExistingSolution = async () => {
        try {
            setIsLoadingExistingSolution(true);
            const response = await searchExerciseService.getExercise(selectedExercise?.id);
            if (response?.solution_content) {
                setUpdatedSolution(response.solution_content);
            }
        } catch (error) {
            console.error("Fetching existing solution failed:", error);
        } finally {
            setIsLoadingExistingSolution(false);
        }
    };


    const handleUpdateSolutionInDB = () => {
        if (!selectedExercise?.id) return;

        setConfirmationData({
            title: t('solutionupdate'),
            message: t('solutionUpdateText'),
            onConfirm: async () => {
                setConfirmationLoading(true);
                try {
                    const payload = {
                        solution_content: updatedSolution || exerciseSolution
                    };
                    const response = await searchExerciseService.updateExercise(selectedExercise?.id, payload);
                    console.log(response);
                    showSuccessToast(t('exerciseUpdatedSuccessfully'));
                } catch (error) {
                    console.error("Updating solution failed:", error);
                    showErrorToast(t('exerciseUpdateFailed'));
                } finally {
                    setConfirmationLoading(false);
                    setConfirmationOpen(false);
                }
            }
        });

        setConfirmationOpen(true);
    };

    const handleChartRepaint = async () => {
        if (!isValidChartRepaintMessage || !selectedQuestionId) return;
      
        try {
          setIsChartRepainting(true);
          setLoadingChartId(selectedQuestionId);
      
          const payload = {
            exercise_id: selectedExercise?.id,
            user_prompt: chartRepaintInput,
            question_id: selectedQuestionId,
          };
      
          const response = await searchExerciseService.createQuestionChart(payload);
      
          if (response?.data?.chart_image) {
            updateChart(selectedQuestionId, response.data.chart_image);

            setChartRepaintInput("");
            showSuccessToast(t('chartRepaintSuccess'));
          } else {
            showErrorToast(t('chartRepaintFailed'));
          }
        } catch (error) {
          console.error("Chart repaint request failed:", error);
          showErrorToast(t('chartRepaintFailed'));
        } finally {
          setIsChartRepainting(false);
          setLoadingChartId(null);
        }
      };

    return (
        <>
            <div className={`excercise_solution_content_container ${exerciseCharts.length > 0 ? 'exercise_charts_section_height' : ''}`}>
                {isSendingRequest ? (
                    <div className="loaderBlock centered">
                        <img src={Loader} alt="Loader" className="large-loader" />
                        <p className="loader-message">{t('fixingAndReturningInSeconds')}</p>
                    </div>
                ) : exerciseSolution !== '' ? (
                    renderedSolution
                ) : (
                    <div className="loaderBlock">
                        <img src={Loader} alt="Loader" />
                    </div>
                )}
            </div>
            <div className={ exerciseCharts.length > 0 ? 'drawer_footer exercise_solution_footer custom-footer' : 'drawer_footer exercise_solution_footer'}>
                <div className="footer_left">
                    <ChatFooter
                        inputMessage={messageInput}
                        setInputMessage={setMessageInput}
                        handleSubmit={handleSendRequest}
                        handleKeyDown={handleKeyDown}
                        isLoading={isSendingRequest}
                        shouldShowButtons={false}
                        titleText={t('typeRepairRequest')}
                        placeholderText={t('enterRepairRequest')}
                        isSendDisabled={!isValidMessage || isSendingRequest}
                        clearInputOnSend={false}
                    />
                </div>
                {exerciseCharts.length > 0 && (
                    <div className="footer_left">
                        <ChatFooter
                            inputMessage={chartRepaintInput}
                            setInputMessage={setChartRepaintInput}
                            handleSubmit={handleChartRepaint}
                            handleKeyDown={handleKeyDown}
                            isLoading={isChartRepainting}
                            shouldShowButtons={false}
                            titleText={t('headerChartRepaintRequest')}
                            placeholderText={t('typeChartRepaintRequest')}
                            isSendDisabled={!isValidChartRepaintMessage || isChartRepainting || selectedQuestionId == null}
                            clearInputOnSend={false}
                            disabled={selectedQuestionId == null}
                        />
                    </div>
                )}
                {exerciseCharts.length > 0 && (
                    <div className="exercise_charts_section">
                        <h6>{t('sketchSolutionPart')}</h6>
                        <ul className="chart-links">
                            {exerciseCharts.map((chart) => {
                                const questionIndex = exerciseQuestions.findIndex(q => q.id === chart.question_id);
                                const hebrewLetter = questionIndex !== -1 ? hebrewLetters[questionIndex] : '?';

                                return (
                                    <li key={chart.question_id} className="chart-item">
                                        <Radio
                                            checked={selectedQuestionId === chart.question_id}
                                            onChange={() => setSelectedQuestionId(chart.question_id)}
                                            value={chart.question_id}
                                        />
                                        <span>{`${t('section')} ${hebrewLetter}`} - </span>
                                        {loadingChartId === chart.question_id ? (
                                            <div className="loaderBlock chart-loading">
                                                {t('drawingNewCartoon')}...
                                                <img src={Loader} alt="Loading..." />
                                            </div>
                                            ) : (
                                            <Link
                                                onClick={async () => {
                                                setLoadingChartId(chart.question_id);
                                                try {
                                                    await onOpenChart(chart.chart_image_base64);
                                                } finally {
                                                    setLoadingChartId(null);
                                                }
                                                }}
                                                className="chart-link-btn"
                                            >
                                                {t('showSketch')}
                                            </Link>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
                <div className="footer_right">
                    <Button
                        text={t('viewExistingSolution')}
                        color='blue'
                        loader={isLoadingExistingSolution}
                        onClick={handleViewExistingSolution}
                    />
                    <Button
                        text={t('updateSolution')}
                        color='blue'
                        loader={isUpdateSolution}
                        onClick={handleUpdateSolutionInDB}
                    />
                    <Button
                        text={t('confirmSolution')}
                        color='blue'
                        loader={isUpdating}
                        onClick={onUpdate}
                        disabled={selectedExercise?.is_solution_approved}
                    />
                </div>
            </div>
            <ConfirmationModal
                open={confirmationOpen}
                title={confirmationData.title}
                message={confirmationData.message}
                loading={confirmationLoading}
                onConfirm={confirmationData.onConfirm}
                onCancel={() => setConfirmationOpen(false)}
                confirmText={t('yes')}
                cancelText={t('no')}
            />
        </>
    );
};

export default ExerciseSolution;
